import React from 'react';
import styled, { keyframes, css } from 'styled-components';

interface GlowEffectsProps {
  type: 'dice' | 'text' | 'button' | 'yatzy' | 'straight' | 'fullhouse' | 'fourOfAKind' | 'threeOfAKind';
  active?: boolean;
  children: React.ReactNode;
}

const pulseGlow = keyframes`
  0% {
    box-shadow: 0 0 5px currentColor;
  }
  50% {
    box-shadow: 0 0 20px currentColor, 0 0 30px currentColor;
  }
  100% {
    box-shadow: 0 0 5px currentColor;
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const rainbow = keyframes`
  0% { filter: hue-rotate(0deg); }
  100% { filter: hue-rotate(360deg); }
`;

const GlowContainer = styled.div<{ $type: string; $active: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  display: block;
  
  ${props => props.$active && props.$type === 'dice' && css`
    &::before {
      content: '';
      position: absolute;
      top: -6px;
      left: -6px;
      right: -6px;
      bottom: -6px;
      background: radial-gradient(circle, rgba(255,215,0,0.4) 0%, transparent 70%);
      border-radius: 14px;
      z-index: -1;
      animation: ${pulseGlow} 1.5s ease-in-out infinite;
    }
  `}
  
  ${props => props.$active && props.$type === 'text' && css`
    color: #FFD700;
    text-shadow: 0 0 8px rgba(255,215,0,0.6);
    animation: ${rainbow} 3s linear infinite;
  `}
  
  ${props => props.$active && props.$type === 'button' && css`
    transform: scale(1.05);
    &::before {
      content: '';
      position: absolute;
      inset: -3px;
      background: linear-gradient(45deg, #FFD700, #FF6B6B, #4ECDC4);
      border-radius: inherit;
      z-index: -1;
      animation: ${shimmer} 2s linear infinite;
      opacity: 0.6;
    }
  `}
  
  ${props => {
    if (!props.$active) return '';
    switch (props.$type) {
      case 'yatzy':
        return css`
          color: #FFD700;
          animation: ${pulseGlow} 1s ease-in-out infinite, ${rainbow} 3s linear infinite;
          &::before {
            content: '';
            position: absolute;
            top: -10px;
            left: -10px;
            right: -10px;
            bottom: -10px;
            background: linear-gradient(45deg, 
              #FFD700, #FF6B6B, #4ECDC4, #45B7D1, #96CEB4, #FFEAA7, #FFD700);
            background-size: 200% 200%;
            border-radius: 50%;
            z-index: -1;
            animation: ${shimmer} 2s linear infinite;
            opacity: 0.7;
          }
        `;
      
      case 'straight':
        return css`
          color: #45B7D1;
          animation: ${pulseGlow} 1.5s ease-in-out infinite;
          &::before {
            content: '';
            position: absolute;
            top: -5px;
            left: -5px;
            right: -5px;
            bottom: -5px;
            background: linear-gradient(90deg, transparent, #45B7D1, transparent);
            background-size: 200% 100%;
            border-radius: 50%;
            z-index: -1;
            animation: ${shimmer} 1.5s ease-in-out infinite;
            opacity: 0.5;
          }
        `;
      
      case 'fullhouse':
        return css`
          color: #96CEB4;
          animation: ${pulseGlow} 2s ease-in-out infinite;
          &::before {
            content: '';
            position: absolute;
            top: -5px;
            left: -5px;
            right: -5px;
            bottom: -5px;
            background: radial-gradient(circle, #96CEB4 0%, transparent 70%);
            border-radius: 50%;
            z-index: -1;
            opacity: 0.6;
          }
        `;
      
      case 'fourOfAKind':
        return css`
          color: #FF6B6B;
          animation: ${pulseGlow} 1.2s ease-in-out infinite;
          &::before {
            content: '';
            position: absolute;
            top: -5px;
            left: -5px;
            right: -5px;
            bottom: -5px;
            background: conic-gradient(#FF6B6B, #FFD700, #FF6B6B);
            border-radius: 50%;
            z-index: -1;
            animation: ${rainbow} 2s linear infinite;
            opacity: 0.5;
          }
        `;
      
      case 'threeOfAKind':
        return css`
          color: #FFEAA7;
          animation: ${pulseGlow} 2.5s ease-in-out infinite;
          &::before {
            content: '';
            position: absolute;
            top: -3px;
            left: -3px;
            right: -3px;
            bottom: -3px;
            background: #FFEAA7;
            border-radius: 50%;
            z-index: -1;
            opacity: 0.3;
          }
        `;
      
      default:
        return '';
    }
  }}
`;

const ParticleContainer = styled.div<{ $showParticles: boolean }>`
  position: relative;
  width:100%;
  height:100%;
  
  ${props => props.$showParticles && css`
    &::after {
      content: '✨';
      position: absolute;
      top: -20px;
      left: 50%;
      transform: translateX(-50%);
      font-size: 16px;
      animation: ${pulseGlow} 1s ease-in-out infinite;
      z-index: 10;
    }
  `}
`;

const GlowEffects: React.FC<GlowEffectsProps> = ({ type, active = true, children }) => {
  // Only show particles for yatzy effects
  const showParticles = active && type === 'yatzy';

  return (
    <GlowContainer $type={type} $active={active}>
      <ParticleContainer $showParticles={showParticles}>
        {children}
      </ParticleContainer>
    </GlowContainer>
  );
};

export default GlowEffects;
